import { PageController } from '@client/components/_core/layout/PageController';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { match } from 'ts-pattern';
import { type IdentifiedCriticalOperation, IdentifiedCriticalOperationForm } from './IdentifiedCriticalOperationForm';
export const Route = createFileRoute(
    '/console/$orgId/configuration/compliance/mspr/critical-operations/identified/add',
)({
    component: AddIdentifiedCriticalOperation,
    staticData: {
        presentation: 'overlay',
    },
});

function AddIdentifiedCriticalOperation() {
    const { trpc, trpcUtils } = useTrpc();
    const [open, setOpen] = useState(true);
    const navigate = useNavigate();
    const orgId = useOrgId();
    const { mutate: createCriticalOperation, isPending: isCreating } =
        trpc.mspr.criticalOperation.createCriticalOperation.useMutation();

    const handleSubmit = (values: IdentifiedCriticalOperation) => {
        const key = 'critical_operation_action';

        if (values.operationMode === 'create') {
            // Create new critical operation
            message.loading({ content: 'Creating…', key });
            createCriticalOperation(
                {
                    name: values.name!,
                    subType: values.subType ?? null,
                    maxTimeToleranceInHours: values.maxTimeToleranceInHours ?? null,
                    dataLossTolerance: values.dataLossTolerance ?? null,
                    serviceLevelTolerance: values.serviceLevelTolerance ?? null,
                },
                {
                    onSuccess: () => {
                        message.success({ content: 'Created', key });
                        trpcUtils.mspr.invalidate();
                        setOpen(false);
                    },
                    onError: (error) => {
                        errorMessage.show(error || 'Failed to create critical operation', key);
                    },
                },
            );
        } else {
            // Create new instance from Critical Operation definition
            message.loading({ content: 'Creating…', key });
            createCriticalOperation(
                {
                    criticalOperationDefinitionId: values.criticalOperationDefinitionId!,
                    subType: values.subType ?? null,
                    maxTimeToleranceInHours: values.maxTimeToleranceInHours ?? null,
                    dataLossTolerance: values.dataLossTolerance ?? null,
                    serviceLevelTolerance: values.serviceLevelTolerance ?? null,
                },
                {
                    onSuccess: () => {
                        message.success({ content: 'Created', key });
                        trpcUtils.mspr.invalidate();
                        setOpen(false);
                    },
                    onError: (error) => {
                        errorMessage.show(error || 'Failed to create critical operation');
                    },
                },
            );
        }
    };

    return (
        <PageController route={Route}>
            <IdentifiedCriticalOperationForm
                open={open}
                mode="add"
                data={undefined}
                state={match({ isCreating })
                    .with({ isCreating: true }, () => 'submitting' as const)
                    .otherwise(() => 'ready' as const)}
                onSubmit={handleSubmit}
                onCloseFinished={() => {
                    navigate({
                        to: '/console/$orgId/configuration/compliance/mspr/critical-operations',
                        params: { orgId },
                    });
                }}
            />
        </PageController>
    );
}
